import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Filler,
    RadialLinearScale,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar, Line, PolarArea } from 'react-chartjs-2';
import baseURL from '../../url';
import './DashboardCharts.css';
import { Link } from 'react-router-dom';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Filler,
    RadialLinearScale,
    Title,
    Tooltip,
    Legend
);

const demoFechas = [
    { label: '01 jun', count: 3 },
    { label: '05 jun', count: 5 },
    { label: '10 jun', count: 2 },
    { label: '15 jun', count: 7 },
    { label: '20 jun', count: 4 },
    { label: '25 jun', count: 6 },
    { label: '01 jul', count: 3 },
    { label: '05 jul', count: 8 },
    { label: '10 jul', count: 5 },
    { label: '15 jul', count: 2 },
];

const demoCategorias = {
    labels: ['Peluqueria', 'Barberia', 'Manicuria', 'Masajes', 'Maquillaje'],
    data: [12, 9, 7, 5, 3],
};

export default function DashboardCharts() {
    const [servicios, setServicios] = useState([]);
    const [categorias, setCategorias] = useState([]);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            const [serviciosRes, categoriasRes] = await Promise.all([
                fetch(`${baseURL}/serviciosGet.php`).then(res => res.json()),
                fetch(`${baseURL}/categoriasGet.php`).then(res => res.json()),
            ]);
            setServicios(serviciosRes.servicios || []);
            setCategorias(categoriasRes.categorias || []);
        } catch (error) {
            console.error('Error al cargar datos:', error);
        }
    };

    const agruparPorFecha = () => {
        const conteo = {};
        servicios.forEach(s => {
            if (!s.createdAt) return;
            const fechaRaw = s.createdAt.split(' ')[0];
            conteo[fechaRaw] = (conteo[fechaRaw] || 0) + 1;
        });

        const fechasOrdenadas = Object.keys(conteo).sort();
        return fechasOrdenadas.map(f => {
            const partes = f.split('-');
            const fecha = new Date(partes[0], partes[1] - 1, partes[2]);
            return {
                fecha: f,
                label: fecha.toLocaleDateString('es', { day: '2-digit', month: 'short' }),
                count: conteo[f],
            };
        });
    };

    const agruparPorCategoria = () => {
        const conteo = {};
        servicios.forEach(s => {
            const cat = categorias.find(c => c.idCategoria === s.idCategoria);
            const nombre = cat ? cat.categoria : 'Sin categoría';
            conteo[nombre] = (conteo[nombre] || 0) + 1;
        });

        const entradas = Object.entries(conteo).sort((a, b) => b[1] - a[1]);
        return {
            labels: entradas.map(e => e[0]),
            data: entradas.map(e => e[1]),
        };
    };

    const fechaData = agruparPorFecha();
    const catData = agruparPorCategoria();

    const usarFechasDemo = fechaData.length === 0;
    const usarCategoriasDemo = catData.labels.length === 0;

    const fechasFinales = usarFechasDemo ? demoFechas : fechaData;
    const catFinales = usarCategoriasDemo ? demoCategorias : catData;

    const createGradient = (ctx) => {
        if (!ctx) return '#2563eb';
        const gradient = ctx.createLinearGradient(0, 0, 0, 260);
        gradient.addColorStop(0, 'rgba(37, 99, 235, 0.35)');
        gradient.addColorStop(1, 'rgba(37, 99, 235, 0.02)');
        return gradient;
    };

    const lineData = {
        labels: fechasFinales.map(d => d.label),
        datasets: [
            {
                label: 'Servicios',
                data: fechasFinales.map(d => d.count),
                borderColor: '#2563eb',
                borderWidth: 2.5,
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    return createGradient(ctx);
                },
                fill: true,
                tension: 0.4,
                pointBackgroundColor: '#2563eb',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 7,
            },
        ],
    };

    const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: {
                backgroundColor: '#fff',
                titleColor: '#1e293b',
                bodyColor: '#64748b',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                displayColors: false,
                callbacks: {
                    label: (ctx) => `${ctx.parsed.y} servicios`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { display: false },
                ticks: {
                    color: '#6b7280',
                    font: { size: 10 },
                    maxRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 10,
                },
            },
            y: {
                beginAtZero: true,
                border: { display: false },
                grid: { color: '#f1f5f9', drawBorder: false },
                ticks: {
                    color: '#6b7280',
                    font: { size: 11 },
                    stepSize: 1,
                    padding: 8,
                },
            },
        },
    };

    const polarData = {
        labels: catFinales.labels,
        datasets: [
            {
                data: catFinales.data,
                backgroundColor: [
                    'rgba(37, 99, 235, 0.35)',
                    'rgba(37, 99, 235, 0.28)',
                    'rgba(37, 99, 235, 0.20)',
                    'rgba(37, 99, 235, 0.35)',
                    'rgba(37, 99, 235, 0.25)',
                ],
                borderColor: '#2563eb',
                borderWidth: 2,
            },
        ],
    };

    const polarOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: 'var(--text-color2)',
                    font: { size: 11 },
                    padding: 12,
                    usePointStyle: true,
                    pointStyleWidth: 8,
                },
            },
            tooltip: {
                backgroundColor: '#fff',
                titleColor: '#1e293b',
                bodyColor: '#64748b',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                callbacks: {
                    label: (ctx) => `${ctx.label}: ${ctx.parsed.r} servicios`,
                },
            },
        },
        scales: {
            r: {
                ticks: { display: false },
                grid: { color: '#e2e8f0' },
            },
        },
    };

    return (
        <div className="dashboardCharts">
            <div className="chartCard">
                <div className="chartHeader">
                    <h3>Servicios</h3>
                    {usarFechasDemo && <span className="chartBadge chartBadge--demo">Vista previa</span>}
                   <Link to='servicios'  className='chartBadge'>
                                                       Ver más
                       </Link>
                </div>
                <div className="chartBody">
                    <Line data={lineData} options={lineOptions} />
                </div>
            </div>

            <div className="chartCard">
                <div className="chartHeader">
                    <h3>Categoría</h3>
                    {usarCategoriasDemo && <span className="chartBadge chartBadge--demo">Vista previa</span>}
                    
                     <Link to='categorias'  className='chartBadge'>
                                                       Ver más
                       </Link>
                   
                </div>
                <div className="chartBody chartBody--polar">
                    <PolarArea data={polarData} options={polarOptions} />
                </div>
            </div>
        </div>
    );
}